import { LoadingSpinner, SubmitOnBlurInputField, SubmitOnSelectSelectField } from "shared"
import { InlineLabelSwitch } from "shared/src/components/shadcn/components/atoms/switches"
import { Button } from "shared/src/components/shadcn/components/atoms/buttons"
import { toast } from "sonner"
// import { USER_DESCRIPTION_OPTIONS } from "shared"
import {
	adminPatchClientUser,
	adminUpdateClientUser,
	adminGetUserInviteStatus,
	adminDeleteClientUser,
	getHumanReadableAge,
	RESTRICTABLE_FEATURES,
	useFormFields,
	UserFromDb,
} from "shared"
import "./UserInfoView.css"
import { useCallback, useEffect, useMemo, useState } from "react"
import { canResendInvite } from "./inviteStatus"
import { USER_DESCRIPTION_OPTIONS } from "shared/src/containers/SignUp/ConfirmAccount/ConfirmAccount"

const CONTACT_TYPES_OPTIONS = ["SuperUser", "Champion", "Account lead", "Admin team"]

// Integrations that store OAuth tokens against the individual user. The others (e.g. jobadder,
// tracker) hold one token set for the whole client, so a single user's tokens can't be cleared.
const PER_USER_OAUTH_PREFIXES = ["bhn", "sfc", "vnc"]

export function ExistingUserInfo({
	clientId,
	user,
	setUserData,
	onUserDeleted,
}: {
	clientId
	user: UserFromDb
	setUserData: (newData: UserFromDb) => void
	onUserDeleted?: (userId: string) => void
}): JSX.Element {
	const [isLoading, setIsLoading] = useState(false)
	const [inviteStatus, setInviteStatus] = useState<string | null>(null)

	useEffect(() => {
		if (user?.externalId == null && user?.userId) {
			setInviteStatus(null)
			adminGetUserInviteStatus({ clientId, userId: user.userId })
				.then((response) => setInviteStatus(response.inviteStatus))
				.catch(() => setInviteStatus(null))
		} else {
			setInviteStatus(null)
		}
	}, [user?.userId, user?.externalId, clientId])

	const [fields, handleFieldChange, updateFields] = useFormFields({
		linkedInUrl: user.linkedInUrl || "",
		contactType: user.contactType || "",
		userDescription: user.userDescription || "",
	})
	useMemo(() => {
		updateFields({
			linkedInUrl: user.linkedInUrl || "",
			contactType: user.contactType || "",
			userDescription: user.userDescription || "",
		})
	}, [user.userId])

	const handleBlurSubmit = (fieldName: string) => async (event: React.FocusEvent<HTMLInputElement>) => {
		await adminPatchClientUser({
			clientId: clientId,
			userId: user.userId,
			updates: { [fieldName]: fields[fieldName] },
		})
		setUserData({ ...user, [fieldName]: fields[fieldName] })
	}
	const handleSubmitOnSelect = async (fieldName: string) => {
		await adminPatchClientUser({
			clientId,
			userId: user.userId,
			updates: { [fieldName]: fields[fieldName] },
		})
		setUserData({ ...user, [fieldName]: fields[fieldName] })
	}
	const handleResendInvitation = async () => {
		setIsLoading(true)

		const response = await adminUpdateClientUser({
			clientId,
			userId: user.userId,
			updateType: "resendInvite",
		})
		setIsLoading(false)
		setUserData({ ...user, userId: response.userId })
	}
	const handleRemoveIntegration = async () => {
		setIsLoading(true)

		await adminPatchClientUser({
			clientId,
			userId: user.userId,
			updates: { externalId: null },
		})
		setIsLoading(false)
		setUserData({ ...user, externalId: null })
	}
	const hasPerUserOauthTokens =
		user?.externalId != null && PER_USER_OAUTH_PREFIXES.includes(user.externalId.slice(0, 3))
	const handleClearOauthTokens = async () => {
		if (
			!window.confirm(
				`Clear the stored integration login for ${user.name}? They will be asked to log in to the integration again.`
			)
		)
			return
		setIsLoading(true)
		try {
			await adminUpdateClientUser({ clientId, userId: user.userId, updateType: "clearOauthTokens" })
			toast.success(`Cleared the stored integration login for ${user.name}`)
		} catch {
			toast.error("Failed to clear stored integration login")
		} finally {
			setIsLoading(false)
		}
	}
	const handleDeleteUser = async () => {
		if (!window.confirm(`Are you sure you want to delete user ${user.name} (${user.email})?`)) return
		setIsLoading(true)
		try {
			await adminDeleteClientUser({ clientId, userId: user.userId, email: user.email })
			onUserDeleted?.(user.userId)
		} catch {
			alert("Failed to delete user")
		} finally {
			setIsLoading(false)
		}
	}
	return (
		<div className="user-info-view-scroll">
			<div className="user-info-section">
				<div>
					<span style={{ fontWeight: 500, width: "9rem", display: "inline-block", lineHeight: "39px" }}>Email:</span>
					<span>{user?.email}</span>
				</div>
				<div>
					<span style={{ fontWeight: 500, display: "inline-block", width: "9rem", lineHeight: "39px" }}>Role:</span>
					<span>{user?.userRole === "brandEditor" ? "brand editor" : user?.userRole}</span>
				</div>
				<div style={{ display: "flex", justifyContent: "left", alignItems: "center", gap: "4rem" }}>
					<div>
						<span style={{ fontWeight: 500, display: "inline-block", width: "9rem", lineHeight: "39px" }}>
							Last Login:
						</span>
						<span>{user?.lastLogin ? `${getHumanReadableAge(user?.lastLogin)} ago` : "Unknown"}</span>
					</div>

					{canResendInvite(inviteStatus) && (
						<Button
							variant="outline"
							onClick={handleResendInvitation}
							style={isLoading ? { cursor: "not-allowed" } : {}}
							loading={isLoading}>
							Resend Invitation
						</Button>
					)}
				</div>
				<div style={{ display: "flex", justifyContent: "left", alignItems: "center", gap: "4rem" }}>
					<div>
						<span style={{ fontWeight: 500, display: "inline-block", width: "9rem", lineHeight: "39px" }}>
							Integration User:
						</span>
						<span>{user?.externalId ? user.externalId.substring(0, 3) : "false"}</span>
					</div>

					{user?.externalId && (
						<Button
							variant="outline"
							onClick={handleRemoveIntegration}
							style={isLoading ? { cursor: "not-allowed" } : {}}
							loading={isLoading}>
							Remove user from integration
						</Button>
					)}

					{hasPerUserOauthTokens && (
						<Button
							variant="outline"
							onClick={handleClearOauthTokens}
							style={isLoading ? { cursor: "not-allowed" } : {}}
							loading={isLoading}>
							Clear stored integration login
						</Button>
					)}
				</div>
				<SubmitOnBlurInputField
					key={"linkedInUrl"}
					label={"LinkedIn URL:"}
					type={"text"}
					placeholder={"Enter LinkedIn URL"}
					initialValue={fields.linkedInUrl}
					inputFieldHandler={handleFieldChange}
					fieldName={"linkedInUrl"}
					onBlur={handleBlurSubmit("linkedInUrl")}
				/>
				<SubmitOnSelectSelectField
					key={"userDescription"}
					label={"User Description:"}
					placeholder={fields.userDescription}
					fieldName={"userDescription"}
					handleSubmitOnSelect={handleSubmitOnSelect}
					field={fields.userDescription}
					updateField={updateFields}
					options={USER_DESCRIPTION_OPTIONS}
				/>
				<SubmitOnSelectSelectField
					key={"contactType"}
					label={"Contact Type:"}
					placeholder={fields.contactType}
					fieldName={"contactType"}
					handleSubmitOnSelect={handleSubmitOnSelect}
					field={fields.contactType}
					updateField={updateFields}
					options={CONTACT_TYPES_OPTIONS}
				/>
				<div style={{ marginTop: "2rem" }}>
					<Button variant="destructive" onClick={handleDeleteUser} loading={isLoading}>
						Delete User
					</Button>
				</div>
			</div>
			<div className="user-info-section">
				<h4 className="bs-h4">Access Management</h4>
				<div>Under construction</div>
			</div>
			<div>
				<h4 className="bs-h4">Beta Access</h4>
				<LabelledToggleSwitch
					label="Beta Site access (All Features)"
					value={user.userType === "beta"}
					onChange={async (newValue) => {
						const newData = await adminUpdateClientUser({
							clientId,
							userId: user.userId,
							betaAccess: newValue,
							updateType: "betaAccess",
						})
						setUserData({ ...newData, userId: user.userId })
					}}
				/>
				<UserBetaPermissions user={user} setUserData={setUserData} clientId={clientId} />
			</div>
		</div>
	)
}

function UserBetaPermissions({
	clientId,
	user,
	setUserData,
}: {
	clientId: string
	user: UserFromDb
	setUserData: (newData: UserFromDb) => void
}): JSX.Element {
	const onChange = useCallback(
		async (permissionKey, newValue) => {
			const newData = await adminPatchClientUser({
				clientId,
				userId: user.userId,
				updates: { betaPermissions: { ...user.betaPermissions, [permissionKey]: newValue } },
			})
			setUserData({ ...newData, userId: user.userId })
		},
		[user.betaPermissions, user.userId, setUserData]
	)

	return (
		<div className="user-info-section">
			<h5 className="bs-h5">Beta Feature Permissions (Access via Main Site)</h5>
			<div>
				{Object.entries(RESTRICTABLE_FEATURES)
					.filter(([, permission]) => permission.releaseState === "beta")
					.map(([permissionKey, permission]) => (
						<UserBetaToggle
							user={user}
							onChange={async (value) => {
								await onChange(permissionKey, value)
							}}
							permissionName={permissionKey}
							permissionLabel={permission.label}
							key={permissionKey}
						/>
					))}
			</div>
		</div>
	)
}

function UserBetaToggle({
	user,
	onChange,
	permissionName,
	permissionLabel,
}: {
	permissionName: string
	permissionLabel: string
	user: UserFromDb
	onChange: (value) => Promise<void>
}) {
	return (
		<LabelledToggleSwitch
			label={permissionLabel}
			value={user.betaPermissions?.[permissionName] ?? false}
			onChange={onChange}
		/>
	)
}

function LabelledToggleSwitch({
	label,
	value,
	onChange,
}: {
	label: string
	value: boolean
	onChange: (value) => Promise<void>
}) {
	const [updating, setUpdating] = useState(false)
	return (
		<div style={{ display: "flex", gap: 10, alignItems: "center", margin: "5px 0" }}>
			<span>{label}:</span>
			<div>
				<InlineLabelSwitch
					checked={value ?? false}
					onCheckedChange={(newValue) => {
						setUpdating(true)
						onChange(newValue).finally(() => {
							setUpdating(false)
						})
					}}
				/>
			</div>
			<LoadingSpinner isLoading={updating} />
		</div>
	)
}
